/**
 * Cabeçalho (sections/header.liquid).
 * - <site-header-bar>: publica a altura em --header-height (usada por painéis e colunas fixas),
 *   liga/desliga o modo transparente conforme a rolagem. Com o dock do celular na página
 *   (<menu-flutuante>, sections/menu-flutuante.liquid) e abaixo de 768px: ao rolar para baixo o cabeçalho
 *   some subindo (data-scroll-hidden, só se fixo) e avisa "header:scroll-hide"; ao rolar para cima volta
 *   e avisa "header:scroll-show" (o dock usa os dois para aparecer/sumir).
 * - <header-nav>: submenus e mega menu no hover (atraso curto ao abrir), toque e teclado.
 */
(function () {
  "use strict";

  if (!window.EssenceBaseComponent) return;

  var OPEN_DELAY = 100; // ms antes de abrir no hover (evita abrir ao só passar o mouse)
  var SCROLL_SOLID = 25; // px de rolagem para o cabeçalho transparente ganhar fundo
  var SCROLL_DELTA = 6; // px mínimos de rolagem numa direção para esconder/mostrar (evita tremer)
  var HIDE_QUERY = "(max-width: 767px)";

  /* ------------------------------------------------------------------ */
  /* <site-header-bar>                                                    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get("site-header-bar")) {
    class SiteHeaderBar extends window.EssenceBaseComponent {
      get eagerInit() {
        return true;
      }

      init() {
        var signal = this.__tdController.signal;
        var self = this;

        this.resizeObserver = new ResizeObserver(function () {
          self.publishHeight();
        });
        this.resizeObserver.observe(this);
        this.publishHeight();

        if (this.hasAttribute("data-transparent")) {
          var ticking = false;
          var update = function () {
            ticking = false;
            self.toggleAttribute("data-solid", window.scrollY > SCROLL_SOLID);
          };
          window.addEventListener(
            "scroll",
            function () {
              if (ticking) return;
              ticking = true;
              requestAnimationFrame(update);
            },
            { passive: true, signal: signal }
          );
          update();
        }

        this.bindScrollHide(signal);
      }

      // Esconde ao rolar para baixo / mostra ao rolar para cima — só no celular e só com o dock na página
      bindScrollHide(signal) {
        var self = this;
        var media = window.matchMedia(HIDE_QUERY);
        var lastY = window.scrollY;
        var ticking = false;
        this.scrollHidden = false;

        var update = function () {
          ticking = false;
          var y = Math.max(window.scrollY, 0);
          var delta = y - lastY;
          var active = media.matches && !!document.querySelector("menu-flutuante");
          var busy = !!document.querySelector("side-sheet[open]") || self.hasAttribute("data-menu-open");

          if (!active || y <= self.offsetHeight) {
            self.setScrollHidden(false);
            lastY = y;
            return;
          }
          if (busy || Math.abs(delta) < SCROLL_DELTA) return;
          self.setScrollHidden(delta > 0);
          lastY = y;
        };

        window.addEventListener(
          "scroll",
          function () {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
          },
          { passive: true, signal: signal }
        );
        media.addEventListener("change", update, { signal: signal });
      }

      setScrollHidden(hidden) {
        if (hidden === this.scrollHidden) return;
        this.scrollHidden = hidden;
        // Só o cabeçalho fixo sobe de fato; o não fixo já saiu da tela, mas o aviso vale para o dock
        this.toggleAttribute("data-scroll-hidden", hidden && this.hasAttribute("data-sticky"));
        this.dispatchEvent(new CustomEvent(hidden ? "header:scroll-hide" : "header:scroll-show", { bubbles: true }));
      }

      publishHeight() {
        var bottom = this.getBoundingClientRect().height;
        document.documentElement.style.setProperty("--header-height", Math.round(bottom) + "px");
      }

      destroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.removeAttribute("data-scroll-hidden");
      }
    }

    customElements.define("site-header-bar", SiteHeaderBar);
  }

  /* ------------------------------------------------------------------ */
  /* <header-nav>                                                         */
  /* ------------------------------------------------------------------ */
  if (!customElements.get("header-nav")) {
    class HeaderNav extends window.EssenceBaseComponent {
      get eagerInit() {
        return true;
      }

      init() {
        var signal = this.__tdController.signal;
        var self = this;
        this.bar = this.closest("site-header-bar");
        this.timers = new WeakMap();

        this.querySelectorAll("[data-header-nav-item]").forEach(function (item) {
          var link = item.querySelector(":scope > a");

          item.addEventListener(
            "pointerenter",
            function (event) {
              if (event.pointerType !== "mouse") return;
              self.schedule(item);
            },
            { signal: signal }
          );
          item.addEventListener(
            "pointerleave",
            function (event) {
              if (event.pointerType !== "mouse") return;
              self.cancel(item);
              self.close(item);
            },
            { signal: signal }
          );

          // Toque: 1º toque abre, 2º toque segue o link
          link.addEventListener(
            "click",
            function (event) {
              if (item.classList.contains("is-open")) return;
              if (self.lastPointer === "mouse") return;
              event.preventDefault();
              self.open(item);
            },
            { signal: signal }
          );

          // Teclado: foco abre, sair do item fecha
          item.addEventListener(
            "focusin",
            function () {
              if (self.lastPointer === "keyboard") self.open(item);
            },
            { signal: signal }
          );
          item.addEventListener(
            "focusout",
            function (event) {
              if (!item.contains(event.relatedTarget)) self.close(item);
            },
            { signal: signal }
          );
        });

        this.addEventListener(
          "pointerdown",
          function (event) {
            self.lastPointer = event.pointerType;
          },
          { signal: signal }
        );
        document.addEventListener(
          "keydown",
          function (event) {
            if (event.key === "Tab") self.lastPointer = "keyboard";
            if (event.key !== "Escape") return;
            var open = self.querySelector(":scope > ul > .is-open");
            if (!open) return;
            self.closeAll();
            open.querySelector(":scope > a").focus();
          },
          { signal: signal }
        );
        document.addEventListener(
          "click",
          function (event) {
            if (!self.contains(event.target)) self.closeAll();
          },
          { signal: signal }
        );
      }

      schedule(item) {
        var self = this;
        this.cancel(item);
        this.timers.set(
          item,
          setTimeout(function () {
            self.open(item);
          }, OPEN_DELAY)
        );
      }

      cancel(item) {
        clearTimeout(this.timers.get(item));
      }

      open(item) {
        // fecha os irmãos (e os abertos de outros ramos)
        var parentList = item.parentElement;
        parentList.querySelectorAll(":scope > .is-open").forEach(
          function (other) {
            if (other !== item) this.close(other);
          }.bind(this)
        );
        item.classList.add("is-open");
        var link = item.querySelector(":scope > a");
        if (link) link.setAttribute("aria-expanded", "true");
        this.syncBar();
      }

      close(item) {
        item.classList.remove("is-open");
        var link = item.querySelector(":scope > a");
        if (link) link.setAttribute("aria-expanded", "false");
        item.querySelectorAll(".is-open").forEach(
          function (child) {
            this.close(child);
          }.bind(this)
        );
        this.syncBar();
      }

      closeAll() {
        this.querySelectorAll(".is-open").forEach(this.close.bind(this));
      }

      // O cabeçalho mostra o fundo escurecido enquanto um mega menu está aberto
      syncBar() {
        if (!this.bar) return;
        this.bar.toggleAttribute("data-mega-open", !!this.querySelector(".header-nav__item--mega.is-open"));
        this.bar.toggleAttribute("data-menu-open", !!this.querySelector(".is-open"));
      }

      onEditorBlockSelect(event) {
        var item = event.target.closest(".header-nav__item");
        if (item) this.open(item);
      }

      onEditorBlockDeselect(event) {
        var item = event.target.closest(".header-nav__item");
        if (item) this.close(item);
      }

      destroy() {
        this.closeAll();
      }
    }

    customElements.define("header-nav", HeaderNav);
  }

})();
